import React from 'react';
import TrackingCell from '../../components/TrackingCell/TrackingCell';
import { PendingDeviceOrder, RescueOrderShipmentGql, RescueOrderShipmentStatus } from '../../lib/rescueQueries';
import { formatDate } from '../../lib/utils';
import CollapsibleSection from './CollapsibleSection';
import shipmentStyles from '../../styles/shipmentTable.module.scss';
import styles from './Devices.module.scss';

const columns = ['Order #', 'Device Count', 'Status', 'Order Date', 'Tracking'];

type VisibleShipmentStatus = Exclude<RescueOrderShipmentStatus, 'CANCELED'>;

const STATUS_LABELS: Record<VisibleShipmentStatus, string> = {
  ORDERED: 'Order Received',
  SHIPPED: 'In Transit',
  DELIVERED: 'Delivered',
};

const STATUS_WITH_DOT: ReadonlySet<VisibleShipmentStatus> = new Set(['ORDERED', 'SHIPPED', 'DELIVERED']);

const STATUS_BADGE_CLASS: Record<VisibleShipmentStatus, string> = {
  ORDERED: styles.statusBadgeOrdered,
  SHIPPED: styles.statusBadgeShipped,
  DELIVERED: styles.statusBadgeDelivered,
};

interface DevicesInTransitTableProps {
  shipments: RescueOrderShipmentGql[];
  /**
   * An order the rescue has filed that Fi hasn't approved yet, shown as the first row. It has no
   * shipment behind it — `rescueOrderShipments` is built from `OrderShipment` rows, and one of
   * those only exists once fulfillment releases the order — so it can't arrive through
   * `shipments` and is threaded in separately.
   */
  pendingOrder?: PendingDeviceOrder | null;
  /**
   * When there are no in-transit shipments: render the section with an empty
   * state instead of collapsing to nothing. Used when the "In Transit" filter
   * is selected on its own, so the panel doesn't vanish.
   */
  showWhenEmpty?: boolean;
}

function isVisible(
  shipment: RescueOrderShipmentGql,
): shipment is RescueOrderShipmentGql & { shipmentStatus: VisibleShipmentStatus } {
  return shipment.shipmentStatus !== 'CANCELED';
}

export default function DevicesInTransitTable({
  shipments,
  pendingOrder = null,
  showWhenEmpty = false,
}: DevicesInTransitTableProps) {
  const visibleShipments = shipments.filter(isVisible);
  // The pending order is a row like any other, so it counts toward the section header and is
  // enough on its own to keep the section from collapsing — a rescue whose only device order is
  // awaiting approval would otherwise see no trace of it here.
  const totalCount = visibleShipments.length + (pendingOrder ? 1 : 0);
  if (totalCount === 0 && !showWhenEmpty) {
    return null;
  }

  return (
    <CollapsibleSection
      title="Shipments"
      count={totalCount}
      variant="info"
      description={
        pendingOrder
          ? 'Your device orders — from approval through delivery.'
          : 'Devices on their way to your rescue — track each shipment from order through delivery.'
      }
    >
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <colgroup>
            <col style={{ width: '24%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '16%' }} />
            <col style={{ width: '20%' }} />
            <col style={{ width: '26%' }} />
          </colgroup>
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {totalCount === 0 && (
              <tr>
                <td colSpan={columns.length} className={styles.emptyState}>
                  No devices in transit
                </td>
              </tr>
            )}
            {pendingOrder && (
              <tr>
                {/* Wrapped to match the shipment rows' cell structure, so row heights agree. */}
                <td>
                  <div>{pendingOrder.orderId}</div>
                </td>
                <td>{pendingOrder.deviceQuantity}</td>
                <td>
                  <span className={styles.statusBadgePendingApproval}>
                    <span className={shipmentStyles.statusBadgeDot} />
                    Pending Approval
                  </span>
                </td>
                <td>{formatDate(pendingOrder.submittedAt)}</td>
                {/* No carrier handoff to track yet, and none until Fi approves it. */}
                <td>{'\u2014'}</td>
              </tr>
            )}
            {visibleShipments.map((shipment, idx) => {
              const [primaryOrder, ...additional] = shipment.orderNumbers;
              return (
                <tr key={`${primaryOrder ?? 'shipment'}-${idx}`}>
                  <td>
                    <div>{primaryOrder ?? '\u2014'}</div>
                    {additional.length > 0 && (
                      <div className={shipmentStyles.orderNumbersList}>+{additional.join(', ')}</div>
                    )}
                  </td>
                  <td>{shipment.deviceCount}</td>
                  <td>
                    <span className={STATUS_BADGE_CLASS[shipment.shipmentStatus]}>
                      {STATUS_WITH_DOT.has(shipment.shipmentStatus) && (
                        <span className={shipmentStyles.statusBadgeDot} />
                      )}
                      {STATUS_LABELS[shipment.shipmentStatus]}
                    </span>
                  </td>
                  <td>{formatDate(shipment.orderDate)}</td>
                  <td>
                    <TrackingCell trackingNumber={shipment.trackingNumber} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </CollapsibleSection>
  );
}
